import { useGlobalModal } from '@/hooks/useGlobalModal';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import GlobalModal from '@components/GlobalModal';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import UserNameSpan from '@components/userNameSpan';
import { Access } from '@features/authorization';
import { t, Trans } from '@lingui/macro';
import { Box, DialogContentText, Typography } from '@mui/material';
import { useEffect, useState, type FC } from 'react';
import RESEND_CREDENTIALS_GET_USER_INFO from '../graphql/queries/getUserInfo';
import { useLingui } from '@lingui/react';
import { RESEND_USER_CREDENTIALS } from '../graphql/mutations/resendUserCredentials';
import { useModalStatusMessage } from '@/hooks';

interface Props {
  userId: number;
  open: boolean;
  onClose: () => void;
}

const SuspenseComponent: FC<Props> = ({ userId, onClose, open }) => {
  const { i18n } = useLingui();
  const [countdown, setCountdown] = useState(0);
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();
  const { data } = useSuspenseQuery(RESEND_CREDENTIALS_GET_USER_INFO, {
    variables: {
      userId,
    },
  });
  const [resendCredentials, { loading }] = useMutation(RESEND_USER_CREDENTIALS, {
    variables: {
      input: {
        userId,
      },
    },
    onCompleted() {
      openModalSuccessMessage(t(i18n)`Se han reenviado las credenciales correctamente.`);
      onClose();
    },
    onError(error) {
      openModalErrorMessage(t(i18n)`Hubo un error al reenviar las credenciales.`);
      console.error(error);
    },
  });

  useEffect(() => {
    if (open) {
      setCountdown(3);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [open]);

  const { fullName, email } = data.user;

  return (
    <>
      <ModalContent>
        <DialogContentText>
          <Trans>
            ¿Generar y enviar nuevas credenciales de acceso? La contraseña actual dejará de
            funcionar inmediatamente.
          </Trans>
        </DialogContentText>
        <Box
          sx={{
            bgcolor: 'action.hover',
            mt: 1,
            p: 1,
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography>
            <strong>Usuario:</strong> {fullName}
          </Typography>
          <Typography>
            <strong>Correo:</strong> {email}
          </Typography>
        </Box>
      </ModalContent>
      <ModalActions
        onClose={onClose}
        submitButton={{
          disabled: countdown > 0,
          label: countdown > 0 ? t(i18n)`Sí, reenviar (${countdown}s)` : t(i18n)`Sí, reenviar`,
          loading,
          onClick: () => resendCredentials(),
        }}
      />
    </>
  );
};

export const ResendCredentialsModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('resendCredentials');

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={{ maxWidth: 'sm' }}
      authorization={{ access: Access.ResendUserCredentials }}
    >
      <ModalTitle
        onClose={onClose}
        title={<Trans>Reenviar credenciales</Trans>}
        subtitle={
          <UserNameSpan
            showId
            userId={state.open ? state.userId : undefined}
            variant="subtitle1"
            color="text.secondary"
            lineHeight={1}
          />
        }
      />
      {state.open && (
        <SuspenseComponent open={state.open} onClose={onClose} userId={state.userId} />
      )}
    </GlobalModal>
  );
};
